import type { ProblemLevel } from '@mathflat/domain/Problem'
import type { AllGradeSubject, Revision } from '@mathflat/domain/SchoolSystem'
import type {
  StudentWorkbookListItemDetail,
  StudentWorkbookRevision,
} from '@mathflat/domain/StudentWorkbook'
import type { CustomSignatureWorkbook, Workbook } from '@mathflat/domain/Workbook'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { addHours } from 'date-fns'

import ExtendedMap from '@/models/ExtendedMap'
import { commonWorkbookService } from '@/service/commonWorkbook.service'
import dateUtils from '@/utils/DateUtils'

import { DTO } from '../DTO'
import { SchoolGrade } from '../Student'

const now = new Date()

export class StudentWorkbook extends DTO {
  id: number //학생-교재 id
  type: Workbook['type'] // PUBLIC(시중교재), SCHOOL(교과서), CUSTOM ( 내 교재 ) 등
  level: ProblemLevel //난이도
  // page: Workbook['page'] //총 페이지 수
  schoolGrade: SchoolGrade
  grade: AllGradeSubject
  revision: Revision
  semester: Workbook['semester'] //학기
  headtitle: string // 커스텀교재 - 교재명
  subtitle: string | null // 커스텀교재 - 소제목
  title: string // 시중교재 - 책 제목 / 커스텀교재 - 학년,학기
  publisher: Workbook['publisher'] //출판사
  autoScored: boolean // 자동채점 여부
  pairFlag: CustomSignatureWorkbook['pairFlag'] // 쌍둥이 인지
  answerPdfUrl: Workbook['answerPdfUrl'] //빠른 정답 url
  createDate: Date //생성일
  academyId?: string //생성 기관 ID(내 교재·커스텀 시그니처 교재)
  teacherId?: string //생성 선생님 ID(내 교재)
  progress: number // 최근 진도의 페이지 번호
  recentRevisionId: number // 최근 진도인 회차의 ID
  studentWorkbook: StudentWorkbookListItemDetail['studentWorkbook'] // 학생 - 교재 정보
  recentRevisionRound: number // 최근 진도인 회차
  recentAssignedDate: Date // 최근 출제 날짜
  roundToRevisionRoundMap: ExtendedMap<string, StudentWorkbookRevision>
  pairX: 1 | 2 | 3 | null
  featureType?: CustomSignatureWorkbook['featureType']
  securingType: Workbook['securingType']
  originWorkbook: StudentWorkbookListItemDetail['originWorkbook']
  answerConfirmed: boolean

  constructor({ workbook, ...data }: StudentWorkbookListItemDetail) {
    super()
    this.id = workbook.id
    this.type = workbook.type
    this.level = workbook.level
    this.schoolGrade = new SchoolGrade({
      schoolType: workbook.schoolType,
      grade: Number(workbook.grade) as SchoolGrade['grade'],
    })
    this.grade = workbook.grade
    this.semester = workbook.semester
    this.revision = workbook.revision
    this.headtitle = workbook.headtitle
    this.title = workbook.title
    this.subtitle = workbook.subtitle
    this.publisher = workbook.publisher
    this.answerPdfUrl = workbook.answerPdfUrl
    this.academyId = 'academyId' in workbook ? workbook.academyId : undefined
    this.teacherId = 'teacherId' in workbook ? workbook.teacherId : undefined
    this.createDate = new Date(workbook.createDatetime)
    this.autoScored = workbook.autoScored
    this.pairFlag = 'pairFlag' in workbook && workbook.pairFlag
    this.progress = data.recentPageNumber
    this.studentWorkbook = data.studentWorkbook
    this.recentRevisionId = data.recentRevisionId
    this.recentRevisionRound = data.recentRevisionRound
    this.recentAssignedDate = new Date(data.recentAssignedDatetime)
    this.roundToRevisionRoundMap = ExtendedMap.fromObj(data.roundToRevisionRoundMap)
    // 서버는 쌍둥이 배수를 1~3 으로만 낸다
    this.pairX = this.pairFlag ? (workbook.pairX as 1 | 2 | 3 | null) : null
    this.featureType = 'featureType' in workbook ? workbook.featureType : undefined
    this.securingType = workbook.securingType || null
    this.originWorkbook = data.originWorkbook
    this.answerConfirmed = 'answerConfirmed' in workbook && workbook.answerConfirmed
  }

  get formatDate() {
    return dateUtils.toDateString(this.createDate, '.')
  }

  get isAssignedOnlyOneRound() {
    return this.roundToRevisionRoundMap.size === 1
  }

  get lastRound() {
    return this.recentRevisionRound
  }

  get isNewAssign() {
    return addHours(this.recentAssignedDate, 24) > now
  }

  get 한글학년학기과목() {
    return SchoolGrade.getKoreanWorkbookGradeSemester({
      type: this.type,
      grade: this.grade,
      semester: this.semester,
      schoolType: this.schoolGrade.schoolType,
    })
  }

  get isInspectBook() {
    return commonWorkbookService.checkInspectBook(this.id)
  }

  // 정책적으로 UI에 표시되어야 할 TITLE 값
  // subtitle의 유무에 따라서 title 또는 titel + subtitle로 노출 시킴
  get uiWorkbookTitle() {
    return `${this.title} ${this.subtitle ? `- ${this.subtitle}` : ''}`
  }

  get korAnswerConfirmed() {
    return this.answerConfirmed ? '지원' : '미지원'
  }

  get korWorkbookType() {
    switch (this.type) {
      case 'PUBLIC':
        return '시중교재'
      case 'CUSTOM':
        return '내 교재'
      case 'CUSTOM_SIGNATURE':
        return '시그니처 교재'
      case 'ACADEMY_GROUP':
        return tJosa('학원그룹교재')
      case 'SCHOOL':
        return '교과서'
      default:
        return ''
    }
  }
}
